import { handleExitCommandWithSetters } from "../../handlers/system/exitHandlers";
import { handleBackCommandWithSetters } from "../../handlers/system/backHandlers";
import { handleAppendInputToOutput } from "../../handlers/input/inputHandlers";
import {
  handleNewPromptAndStep,
  handleStepComplete,
} from "../../handlers/output/promptHandlers";
import { handleStartNewCommand } from "../../handlers/output/outputHandlers";
import {
  handleRedirectCommand,
  processRedirectResult,
} from "../../handlers/command/redirectHandlers";
import { handleSpecialOutput } from "../core/commandFlowUtils";
import { outputSuggestions, getCommandSuggestions } from "./commandLineUtils";
import { processMultiLineOutput } from "../../utils/ui/outputProcessingUtils";
import { updateSessionState } from "../../utils/session/storeStateUtils";
import useCommandFlowStore from "../../store/commandFlowStore";
import { getTranslationFunction } from "../../utils/core/translationUtils";

export async function handleSessionInput({
  trimmed,
  currentPrompt,
  engine,
  setters,
  resolvePrompt,
  resolveOutput,
  commands,
  onStepStart,
  onStepComplete,
  onCommandComplete,
  onCommandStart,
  onCommandAbort,
  onCommandBack,
  initMessage,
}) {
  // Save command name BEFORE any processing, as engine.handleInput() may end the session
  const activeCommandName = engine.currentCommandName;

  // Handle exit command
  if (
    await handleExitCommandWithSetters({
      trimmed,
      currentPrompt,
      setters,
      engine,
      onCommandAbort,
      activeCommandName,
    })
  ) {
    return;
  }

  // Handle back command
  if (
    await handleBackCommandWithSetters({
      trimmed,
      currentPrompt,
      setters,
      engine,
      onCommandBack,
      activeCommandName,
    })
  ) {
    return;
  }

  // Append input to output
  if (currentPrompt) {
    handleAppendInputToOutput({ currentPrompt, trimmed, setters });
  } else {
    handleAppendInputToOutput({ currentPrompt: null, trimmed, setters });
  }

  const setInSession = useCommandFlowStore.getState().setInSession;

  // Clear prompt and set loading atomically to prevent the default ">"
  // prompt from flashing between two separate state updates.
  useCommandFlowStore.setState({ currentPrompt: null, isLoading: true });
  const result = await engine.handleInput(trimmed);

  // Internal-redirect detection: when a step's `redirect` returns a target
  // and there's no step output, FlowProcessor calls engine.startCommand()
  // directly. The result then carries `prompt`/`output` of the new command
  // but NOT `nextCommand`, so the explicit redirect path below never runs.
  // Detect the change of currentCommandName and fire onCommandStart so
  // consumers (verification engines, telemetry) see the sub-command entry
  // — same latency-sensitive contract as the top-level path.
  if (
    onCommandStart &&
    !result.nextCommand &&
    engine.currentCommandName &&
    engine.currentCommandName !== activeCommandName
  ) {
    try {
      const sessionDataSnapshot =
        useCommandFlowStore.getState().sessionData ?? {};
      onCommandStart(engine.currentCommandName, sessionDataSnapshot);
    } catch (err) {
      console.error("onCommandStart threw (internal redirect):", err);
    }
  }

  // If the result has output that will be typed, pre-set isTyping to prevent
  // the LoadingIndicator from flashing during the async handler processing
  // (multiple awaits between here and addOutputWithTypingEffect starting).
  if (result.output && !result.nextCommand) {
    useCommandFlowStore.getState().setIsTyping(true);
  }

  // Ensure session state is synchronized after processing
  const engineIsInSession = engine.isInSession();
  const storeIsInSession = useCommandFlowStore.getState().inSession;
  if (storeIsInSession !== engineIsInSession) {
    console.log("🔄 Synchronizing session state after input:", {
      engineIsInSession,
      storeIsInSession,
    });
    setInSession(engineIsInSession);
  }

  // Handle redirect with stepOutro case
  if (result.nextCommand) {
    try {
      const redirectResult = await handleRedirectCommand(
        result,
        engine,
        activeCommandName
      );

      await processRedirectResult(redirectResult, {
        resolvePrompt,
        onStepStart,
        onCommandComplete,
        onCommandStart,
        initMessage,
        setters,
        engine,
        nextCommandName: result.nextCommand,
      });
    } catch (error) {
      console.error("❌ Error in redirect command execution:", error);
      // Ensure session state is cleaned up even on error
      updateSessionState(false, undefined);
      setInSession(false);
      setters.setCurrentPrompt(null);
      try { engine.endSession(); } catch (_) { /* already ended */ }
      return;
    }
    return;
  }

  // Check for special output first
  if (
    await handleSpecialOutput({
      result,
      setCurrentPrompt: setters.setCurrentPrompt,
      setInSession,
      initMessage,
      onCommandComplete,
      incrementResetToken: setters.incrementResetToken,
      engine,
    })
  ) {
    updateSessionState(false, undefined); // Clear loading state
    return;
  }

  // Handle new prompt and step
  if (
    await handleNewPromptAndStep({
      result,
      setCurrentPrompt: setters.setCurrentPrompt,
      resolvePrompt,
      onStepStart,
      engine,
    })
  ) {
    // Loading state will be cleared by the prompt typing effect
    return;
  }

  // Handle step completion
  await handleStepComplete({
    result,
    setCurrentPrompt: setters.setCurrentPrompt,
    resolveOutput,
    resolvePrompt,
    onStepComplete,
    onCommandComplete,
    engine,
    commands,
    activeCommandName, // Pass the saved command name
  });

  // Ensure loading is cleared after handling step completion
  // (typing effects should have cleared it, but ensure it's done)
  updateSessionState(false, undefined);
}

export async function handleNewCommand({
  trimmed,
  setters,
  engine,
  resolvePrompt,
  onStepStart,
  onCommandComplete,
  onCommandStart,
  initMessage,
  commands,
}) {
  const { handled, result } = await handleStartNewCommand({
    trimmed,
    engine,
    resolvePrompt,
    setCurrentPrompt: setters.setCurrentPrompt,
    onStepStart,
    onCommandStart,
  });

  if (handled) {
    return;
  }

  const setInSession = useCommandFlowStore.getState().setInSession;

  // Handle redirect with stepOutro case
  if (result.nextCommand) {
    try {
      const redirectResult = await handleRedirectCommand(
        result,
        engine,
        trimmed
      );

      await processRedirectResult(redirectResult, {
        resolvePrompt,
        onStepStart,
        onCommandComplete,
        onCommandStart,
        initMessage,
        setters,
        engine,
        nextCommandName: result.nextCommand,
      });
    } catch (error) {
      console.error("❌ Error in redirect command execution:", error);
      updateSessionState(false, undefined);
      setInSession(false);
      setters.setCurrentPrompt(null);
      try { engine.endSession(); } catch (_) { /* already ended */ }
      return;
    }
    return;
  }

  // Handle special output
  if (
    await handleSpecialOutput({
      result,
      setCurrentPrompt: setters.setCurrentPrompt,
      setInSession,
      initMessage,
      onCommandComplete,
      incrementResetToken: setters.incrementResetToken,
      engine,
    })
  ) {
    return;
  }

  // Handle errors and suggestions
  if (result.error) {
    const suggestions = getCommandSuggestions(commands, trimmed);
    outputSuggestions({
      error: result.error,
      suggestions,
    });
  } else if (result.output) {
    const addOutputWithTypingEffect =
      useCommandFlowStore.getState().addOutputWithTypingEffect;

    await processMultiLineOutput(result.output, addOutputWithTypingEffect);

    if (onCommandComplete) {
      const cmdName = engine.currentCommandName || trimmed;
      const sessionData = engine.sessionManager?.steplessSessionData || {};
      onCommandComplete(cmdName, sessionData);
    }
  }
}
